import React, { useEffect, useState, useCallback } from 'react';
import Footer from '../Components/Footer';
import Auth from '../Function/Auth';
import Header from '../Components/Header';
import { toast, ToastContainer } from "react-toastify";
import { QRCodeSVG } from "qrcode.react";
import axios from 'axios';
import Web3 from 'web3';
import './MasterWallet.css';

// Binance Smart Chain configuration with fallback RPC endpoints
const BSC_RPC_URLS = [
  'https://bsc-dataseed1.binance.org/',
  'https://bsc-dataseed2.binance.org/',
  'https://bsc-dataseed3.binance.org/',
  'https://rpc.ankr.com/bsc'
];

// BEP20 Token ABI (minimal for balance and metadata checking)
const BEP20_ABI = [
  {
    "constant": true,
    "inputs": [{ "name": "_owner", "type": "address" }],
    "name": "balanceOf",
    "outputs": [{ "name": "balance", "type": "uint256" }],
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "decimals",
    "outputs": [{ "name": "", "type": "uint8" }],
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "name",
    "outputs": [{ "name": "", "type": "string" }],
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "symbol",
    "outputs": [{ "name": "", "type": "string" }],
    "type": "function"
  }
];

// Token Addresses on BSC
const TOKEN_ADDRESSES = {
  NMRC: '0x2643815F6603e2b3803D3e6f82706CC13B484a1f',
  USDT: '0x55d398326f99059fF775485246999027B3197955'
};

// Fetch BNB Balance using Web3
const fetchBNBBalance = async (web3Instance, address) => {
  try {
    if (!web3Instance) return "0.00";
    const balance = await web3Instance.eth.getBalance(address);
    const bnb = web3Instance.utils.fromWei(balance, 'ether');
    return parseFloat(bnb).toFixed(6);
  } catch (err) {
    console.error('Error fetching BNB balance:', err);
    return "0.00";
  }
};

// Fetch BEP20 Token Balance using Web3
const fetchTokenBalance = async (web3Instance, tokenAddress, address, decimals = 18) => {
  try {
    if (!web3Instance) return "0.00";
    const contract = new web3Instance.eth.Contract(BEP20_ABI, tokenAddress);
    const balance = await contract.methods.balanceOf(address).call();

    // Convert balance properly using decimals
    const adjustedBalance = parseFloat(balance.toString()) / Math.pow(10, decimals);
    return adjustedBalance.toFixed(6);
  } catch (err) {
    console.error(`Error fetching token balance for ${tokenAddress}:`, err);
    return "0.00";
  }
};

// Get token decimals with fallbacks
const getTokenDecimals = async (web3Instance, tokenAddress) => {
  try {
    if (!web3Instance) return 18;
    const contract = new web3Instance.eth.Contract(BEP20_ABI, tokenAddress);
    const decimals = await contract.methods.decimals().call();
    return parseInt(decimals, 10);
  } catch (err) {
    console.error('Error fetching token decimals:', err);
    return 18; // Default 18 decimals for NMRC and BSC USDT
  }
};

function MasterWallet() {
  const authToken = sessionStorage.getItem('authToken');
  const baseurl = process.env.REACT_APP_BASE_API_URL;
  const [walletAddress, setWalletAddress] = useState("");
  const [bnbBalance, setBnbBalance] = useState("0.00");
  const [usdtBalance, setUsdtBalance] = useState("0.00");
  const [nmrcBalance, setNmrcBalance] = useState("0.00");
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [error, setError] = useState("");
  const [web3, setWeb3] = useState(null);
  const [rpcIndex, setRpcIndex] = useState(0);
  const [currentBlock, setCurrentBlock] = useState(null);

  // Modal states
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [newWalletAddress, setNewWalletAddress] = useState("");
  const [walletPrivateKey, setWalletPrivateKey] = useState("");

  // Initialize Web3 with fallback RPC endpoints
  const initializeWeb3 = useCallback(async () => {
    try {
      const Web3Constructor = Web3.Web3 || Web3;
      const web3Instance = new Web3Constructor(BSC_RPC_URLS[rpcIndex]);

      // Test connection
      const blockNumber = await web3Instance.eth.getBlockNumber();
      setCurrentBlock(Number(blockNumber));
      setWeb3(web3Instance);
      setError("");
      console.log(`Connected to BSC via: ${BSC_RPC_URLS[rpcIndex]} (Block: ${blockNumber})`);
    } catch (err) {
      console.error(`Failed to connect to ${BSC_RPC_URLS[rpcIndex]}:`, err);

      // Try next RPC endpoint
      if (rpcIndex < BSC_RPC_URLS.length - 1) {
        setRpcIndex(prev => prev + 1);
      } else {
        setError('Unable to connect to Binance Smart Chain');
        toast.error('Failed to connect to BSC network');
      }
    }
  }, [rpcIndex]);

  // Fetch Master Wallet Address from API
  const fetchWalletAddress = useCallback(async () => {
    try {
      const response = await axios.get(`${baseurl}/withdraw-wallet`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        params: { network: 'BEP20' }
      });

      if (response.data && response.data.status && response.data.data) {
        const address = response.data.data.withdraw_address || "";
        if (address) {
          setWalletAddress(address);
        }
      }
    } catch (err) {
      console.error('Error fetching master wallet:', err);
    }
  }, [authToken, baseurl]);

  useEffect(() => {
    initializeWeb3();
    fetchWalletAddress();
  }, [initializeWeb3, fetchWalletAddress]);

  // Retry Web3 connection if failed
  useEffect(() => {
    if (!web3 && rpcIndex < BSC_RPC_URLS.length - 1) {
      const timer = setTimeout(() => {
        initializeWeb3();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [rpcIndex, web3, initializeWeb3]);

  // Main function to fetch all balances using Web3
  const fetchBalancesWithWeb3 = useCallback(async () => {
    if (!walletAddress || !web3) {
      return;
    }

    setBalanceLoading(true);
    setError("");

    try {
      // 1. Fetch BNB Balance (native gas currency)
      const bnbBal = await fetchBNBBalance(web3, walletAddress);
      setBnbBalance(bnbBal);

      // 2. Fetch NMRC Balance (Neo MarcLife BEP20)
      try {
        const nmrcDecimals = await getTokenDecimals(web3, TOKEN_ADDRESSES.NMRC);
        const nmrcBal = await fetchTokenBalance(web3, TOKEN_ADDRESSES.NMRC, walletAddress, nmrcDecimals);
        setNmrcBalance(nmrcBal);
      } catch (nmrcErr) {
        console.error('Error fetching NMRC balance:', nmrcErr);
        setNmrcBalance("0.00");
      }

      // 3. Fetch USDT Balance (Tether USD BEP20)
      try {
        const usdtDecimals = await getTokenDecimals(web3, TOKEN_ADDRESSES.USDT);
        const usdtBal = await fetchTokenBalance(web3, TOKEN_ADDRESSES.USDT, walletAddress, usdtDecimals);
        setUsdtBalance(usdtBal);
      } catch (usdtErr) {
        console.error('Error fetching USDT balance:', usdtErr);
        setUsdtBalance("0.00");
      }
    } catch (err) {
      console.error('Fatal error fetching balances with Web3:', err);
      setError('Failed to fetch balances: ' + err.message);
      toast.error('Failed to fetch balances from blockchain');
    } finally {
      setBalanceLoading(false);
    }
  }, [walletAddress, web3]);

  useEffect(() => {
    if (walletAddress && web3) {
      fetchBalancesWithWeb3();
    }
  }, [walletAddress, web3, fetchBalancesWithWeb3]);

  const copyToClipboard = (text, message = "Copied to clipboard!") => {
    if (text) {
      navigator.clipboard.writeText(text)
        .then(() => toast.success(message))
        .catch(() => toast.error("Failed to copy"));
    }
  };

  // Update wallet address function
  const updateWalletAddress = async () => {
    if (!newWalletAddress.trim()) {
      toast.error('Please enter a wallet address');
      return;
    }

    if (!walletPrivateKey.trim()) {
      toast.error('Please enter wallet private key');
      return;
    }

    // Basic validation for wallet address format
    if (!newWalletAddress.startsWith('0x') || newWalletAddress.length !== 42) {
      toast.error('Please enter a valid BEP20 wallet address (42 characters starting with 0x)');
      return;
    }

    setUpdateLoading(true);

    try {
      const updateData = {
        withdrawAddress: newWalletAddress,
        withdrawKey: walletPrivateKey,
      };

      const response = await axios.post(`${baseurl}/update-withdraw-wallet`, updateData, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        }
      });

      if (response.data.status) {
        toast.success('Master wallet updated successfully!');
        setWalletAddress(newWalletAddress);
        setShowUpdateModal(false);
        setNewWalletAddress("");
        setWalletPrivateKey("");

        // Refresh balances with new wallet
        setTimeout(() => {
          fetchBalancesWithWeb3();
        }, 1000);
      } else {
        toast.error(response.data.message || 'Failed to update wallet');
      }
    } catch (err) {
      console.error('Error updating wallet:', err);
      toast.error('Failed to update wallet: ' + (err.response?.data?.message || err.message));
    } finally {
      setUpdateLoading(false);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowUpdateModal(false);
    setNewWalletAddress("");
    setWalletPrivateKey("");
  };

  return (
    <>
      <Header>
        <Auth />
        <div className="main-content app-content">
          <div className="container-fluid">
            {/* Page Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
              <div>
                <h4 className="mb-0 fw-bold text-primary">
                  <i className="fas fa-wallet me-2"></i>Master Wallet Dashboard
                </h4>
                <p className="text-muted mb-0">Live BEP20 blockchain balances for NMRC and USDT</p>
              </div>
              <div className="d-flex gap-2">

                <button
                  onClick={fetchBalancesWithWeb3}
                  className="btn btn-primary btn-sm refresh-btn shadow-sm"
                  disabled={balanceLoading}
                >
                  <i className={`fas fa-sync-alt me-1 ${balanceLoading ? 'fa-spin' : ''}`}></i>
                  {balanceLoading ? 'Fetching Web3...' : 'Refresh Balances'}
                </button>
              </div>
            </div>

            {/* Balance Cards Row */}
            <div className="row g-3 mb-4">
              {/* NMRC Balance Card */}
              <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12">
                <div className="card custom-card border-0 shadow-sm balance-card h-100">
                  <div
                    className="card-body text-center p-4"
                    style={{
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #9333ea 100%)',
                      borderRadius: '16px'
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="badge bg-white bg-opacity-25 text-dark px-2 py-1">
                        <i className="fas fa-coins me-1"></i>Core Token
                      </span>
                      <div className="rounded-circle bg-white p-2 floating-icon shadow-sm d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px' }}>
                        <i className="fas fa-gem text-primary fa-lg"></i>
                      </div>
                    </div>
                    <div className="my-2">
                      <h2 className="text-white fw-bold mb-1">
                        {balanceLoading ? (
                          <div className="spinner-border spinner-border-sm text-white" role="status"></div>
                        ) : (
                          `${parseFloat(nmrcBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 6 })} NMRC`
                        )}
                      </h2>
                      <h6 className="text-white-50 mb-2">Neo MarcLife</h6>
                    </div>
                    <div className="pt-2 border-top border-white border-opacity-10 d-flex justify-content-between align-items-center">
                      <span className="text-white-75 small">
                        <i className="fas fa-network-wired me-1"></i>BSC (BEP20)
                      </span>
                      <a
                        href={`https://bscscan.com/token/${TOKEN_ADDRESSES.NMRC}?a=${walletAddress}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-dark small text-decoration-none bg-white bg-opacity-20 px-2 py-1 rounded"
                        title="View NMRC on BscScan"
                      >
                        <i className="fas fa-external-link-alt me-1"></i>BscScan
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* USDT Balance Card */}
              <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12">
                <div className="card custom-card border-0 shadow-sm balance-card h-100">
                  <div
                    className="card-body text-center p-4"
                    style={{
                      background: 'linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)',
                      borderRadius: '16px'
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="badge bg-white bg-opacity-25 text-dark px-2 py-1">
                        <i className="fas fa-dollar-sign me-1"></i>Stablecoin
                      </span>
                      <div className="rounded-circle bg-white p-2 floating-icon shadow-sm d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px' }}>
                        <img src="assets/images/logo/usdtLogo.png" alt="USDT" width="30" height="30" />
                      </div>
                    </div>
                    <div className="my-2">
                      <h2 className="text-white fw-bold mb-1">
                        {balanceLoading ? (
                          <div className="spinner-border spinner-border-sm text-white" role="status"></div>
                        ) : (
                          `${parseFloat(usdtBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} USDT`
                        )}
                      </h2>
                      <h6 className="text-white-50 mb-2">Tether USD</h6>
                    </div>
                    <div className="pt-2 border-top border-white border-opacity-10 d-flex justify-content-between align-items-center">
                      <span className="text-white-75 small">
                        <i className="fas fa-shield-alt me-1"></i>BEP20 Token
                      </span>
                      <a
                        href={`https://bscscan.com/token/${TOKEN_ADDRESSES.USDT}?a=${walletAddress}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-dark small text-decoration-none bg-white bg-opacity-20 px-2 py-1 rounded"
                        title="View USDT on BscScan"
                      >
                        <i className="fas fa-external-link-alt me-1"></i>BscScan
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* BNB Balance Card (Gas Reserve) */}
              <div className="col-xl-4 col-lg-4 col-md-12 col-sm-12">
                <div className="card custom-card border-0 shadow-sm balance-card h-100">
                  <div
                    className="card-body text-center p-4"
                    style={{
                      background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 50%, #fbbf24 100%)',
                      borderRadius: '16px'
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="badge bg-white bg-opacity-25 text-dark px-2 py-1">
                        <i className="fas fa-gas-pump me-1"></i>Gas Reserve
                      </span>
                      <div className="rounded-circle bg-white p-2 floating-icon shadow-sm d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px' }}>
                        <img src="assets/images/logo/binance.svg" alt="BNB" width="30" height="30" />
                      </div>
                    </div>
                    <div className="my-2">
                      <h2 className="text-white fw-bold mb-1">
                        {balanceLoading ? (
                          <div className="spinner-border spinner-border-sm text-white" role="status"></div>
                        ) : (
                          `${parseFloat(bnbBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 6 })} BNB`
                        )}
                      </h2>
                      <h6 className="text-white-50 mb-2">Binance Coin</h6>
                    </div>
                    <div className="pt-2 border-top border-white border-opacity-10 d-flex justify-content-between align-items-center">
                      <span className="text-white-75 small">
                        <i className="fas fa-fire me-1"></i>BSC Native Gas
                      </span>
                      <a
                        href={`https://bscscan.com/address/${walletAddress}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-dark small text-decoration-none bg-white bg-opacity-20 px-2 py-1 rounded"
                        title="View BNB on BscScan"
                      >
                        <i className="fas fa-external-link-alt me-1"></i>BscScan
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Network & Node Status Banner */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card custom-card border-0 shadow-sm bg-white">
                  <div className="card-body py-3 px-4 d-md-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center mb-2 mb-md-0">
                      <div className="rounded-circle bg-success bg-opacity-10 text-success p-2 me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                        <i className="fas fa-check-circle fa-lg"></i>
                      </div>
                      <div>
                        <h6 className="mb-0 fw-bold">Binance Smart Chain (BEP20 Mainnet)</h6>
                        <small className="text-muted">
                          RPC Node: <code>{BSC_RPC_URLS[rpcIndex]}</code>
                          {currentBlock && ` • Block #${currentBlock.toLocaleString()}`}
                        </small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <span className="badge bg-success-transparent text-success border border-success px-3 py-2">
                        <i className="fas fa-wifi me-1"></i>
                        {web3 ? 'Web3 Connected' : 'Connecting to BSC...'}
                      </span>
                      <span className="badge bg-primary-transparent text-primary border border-primary px-3 py-2">
                        <i className="fas fa-cube me-1"></i>Chain ID 56
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Debug/Error alert if any */}
            {error && (
              <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
                <i className="fas fa-exclamation-triangle me-2 fa-lg"></i>
                <div>
                  <strong>RPC Connection Issue:</strong> {error}
                </div>
              </div>
            )}

            {/* Wallet Info & Assets Row */}
            <div className="row g-4">
              {/* Left Column: Wallet QR + Address Info */}
              <div className="col-xl-7 col-lg-7">
                <div className="card custom-card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-bottom py-3">
                    <h5 className="card-title mb-0 fw-bold text-dark">
                      <i className="fas fa-qrcode me-2 text-primary"></i>Master Wallet Details
                    </h5>
                  </div>
                  <div className="card-body p-4">
                    <div className="row align-items-center">
                      <div className="col-md-5 text-center mb-3 mb-md-0">
                        <div className="p-3 bg-light rounded-3 d-inline-block shadow-sm border">
                          <QRCodeSVG
                            value={walletAddress || '0x0000000000000000000000000000000000000000'}
                            size={160}
                            bgColor="#ffffff"
                            fgColor="#000000"
                            level="M"
                            includeMargin={false}
                          />
                        </div>
                        <p className="text-muted small mt-2 mb-0">Scan to receive BEP20 tokens</p>
                      </div>

                      <div className="col-md-7">
                        <div className="mb-3">
                          <label className="form-label text-muted small fw-semibold text-uppercase mb-1">
                            Master Wallet Address (BEP20)
                          </label>
                          <div className="p-2 bg-light rounded border d-flex align-items-center justify-content-between">
                            <code className="text-dark small text-break fw-bold pe-2">
                              {walletAddress || 'No address configured'}
                            </code>
                            <div className="d-flex gap-1 flex-shrink-0">
                              <button
                                onClick={() => copyToClipboard(walletAddress, "Master wallet address copied!")}
                                className="btn btn-sm btn-outline-primary"
                                title="Copy Address"
                                disabled={!walletAddress}
                              >
                                <i className="fas fa-copy"></i>
                              </button>
                              {walletAddress && (
                                <a
                                  href={`https://bscscan.com/address/${walletAddress}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-sm btn-outline-secondary"
                                  title="View on BscScan"
                                >
                                  <i className="fas fa-external-link-alt"></i>
                                </a>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="p-3 bg-primary bg-opacity-10 rounded border border-primary border-opacity-25">
                          <h6 className="fw-bold text-primary mb-1">
                            <i className="fas fa-info-circle me-1"></i>Automated Settlement Vault
                          </h6>
                          <p className="text-muted small mb-0">
                            This master wallet distributes withdrawals and handles incoming BEP20 operations for both <strong>NMRC</strong> and <strong>USDT</strong>. Always keep sufficient BNB for gas.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Asset Portfolio Table */}
                    <div className="mt-4 pt-3 border-top">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h6 className="fw-bold text-dark mb-0">
                          <i className="fas fa-coins me-2 text-warning"></i>Live Token Portfolio
                        </h6>
                        <span className="badge bg-light text-muted border">Direct Web3 Query</span>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Asset</th>
                              <th>Name</th>
                              <th>Balance</th>
                              <th>Network</th>
                              <th>Contract</th>
                              <th className="text-end">Explorer</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* NMRC Row */}
                            <tr>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="rounded-circle bg-primary text-white p-2 me-2 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '32px', height: '32px' }}>
                                    <i className="fas fa-gem fa-sm"></i>
                                  </div>
                                  <strong className="text-dark">NMRC</strong>
                                </div>
                              </td>
                              <td>Neo MarcLife</td>
                              <td>
                                <span className="fw-bold text-primary">
                                  {balanceLoading ? (
                                    <span className="spinner-border spinner-border-sm text-primary"></span>
                                  ) : (
                                    `${parseFloat(nmrcBalance || 0).toFixed(6)} NMRC`
                                  )}
                                </span>
                              </td>
                              <td><span className="badge bg-primary-transparent text-primary">BEP20</span></td>
                              <td>
                                <span className="font-monospace small text-muted">
                                  {`${TOKEN_ADDRESSES.NMRC.slice(0, 6)}...${TOKEN_ADDRESSES.NMRC.slice(-4)}`}
                                </span>
                                <button
                                  className="btn btn-sm btn-link text-primary p-0 ms-1"
                                  onClick={() => copyToClipboard(TOKEN_ADDRESSES.NMRC, "NMRC contract copied!")}
                                  title="Copy NMRC Contract Address"
                                >
                                  <i className="fas fa-copy"></i>
                                </button>
                              </td>
                              <td className="text-end">
                                <a
                                  href={`https://bscscan.com/token/${TOKEN_ADDRESSES.NMRC}?a=${walletAddress}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-sm btn-light border"
                                >
                                  <i className="fas fa-external-link-alt text-muted"></i>
                                </a>
                              </td>
                            </tr>

                            {/* USDT Row */}
                            <tr>
                              <td>
                                <div className="d-flex align-items-center">
                                  <img src="assets/images/logo/usdtLogo.png" alt="USDT" width="32" height="32" className="me-2 rounded-circle" />
                                  <strong className="text-dark">USDT</strong>
                                </div>
                              </td>
                              <td>Tether USD</td>
                              <td>
                                <span className="fw-bold text-success">
                                  {balanceLoading ? (
                                    <span className="spinner-border spinner-border-sm text-success"></span>
                                  ) : (
                                    `${parseFloat(usdtBalance || 0).toFixed(4)} USDT`
                                  )}
                                </span>
                              </td>
                              <td><span className="badge bg-success-transparent text-success">BEP20</span></td>
                              <td>
                                <span className="font-monospace small text-muted">
                                  {`${TOKEN_ADDRESSES.USDT.slice(0, 6)}...${TOKEN_ADDRESSES.USDT.slice(-4)}`}
                                </span>
                                <button
                                  className="btn btn-sm btn-link text-success p-0 ms-1"
                                  onClick={() => copyToClipboard(TOKEN_ADDRESSES.USDT, "USDT contract copied!")}
                                  title="Copy USDT Contract Address"
                                >
                                  <i className="fas fa-copy"></i>
                                </button>
                              </td>
                              <td className="text-end">
                                <a
                                  href={`https://bscscan.com/token/${TOKEN_ADDRESSES.USDT}?a=${walletAddress}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-sm btn-light border"
                                >
                                  <i className="fas fa-external-link-alt text-muted"></i>
                                </a>
                              </td>
                            </tr>

                            {/* BNB Gas Row */}
                            <tr>
                              <td>
                                <div className="d-flex align-items-center">
                                  <img src="assets/images/logo/binance.svg" alt="BNB" width="32" height="32" className="me-2 rounded-circle" />
                                  <strong className="text-dark">BNB</strong>
                                </div>
                              </td>
                              <td>Binance Coin (Gas)</td>
                              <td>
                                <span className="fw-bold text-warning">
                                  {balanceLoading ? (
                                    <span className="spinner-border spinner-border-sm text-warning"></span>
                                  ) : (
                                    `${parseFloat(bnbBalance || 0).toFixed(6)} BNB`
                                  )}
                                </span>
                              </td>
                              <td><span className="badge bg-warning-transparent text-warning">Native</span></td>
                              <td>
                                <span className="text-muted small">Native Currency</span>
                              </td>
                              <td className="text-end">
                                <a
                                  href={`https://bscscan.com/address/${walletAddress}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-sm btn-light border"
                                >
                                  <i className="fas fa-external-link-alt text-muted"></i>
                                </a>
                              </td>
                            </tr>

                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Quick Stats & Security Notice */}
              <div className="col-xl-5 col-lg-5">
                {/* Quick Stats Card */}
                <div className="card custom-card border-0 shadow-sm mb-4">
                  <div className="card-header bg-white border-bottom py-3">
                    <h5 className="card-title mb-0 fw-bold text-dark">
                      <i className="fas fa-tachometer-alt me-2 text-info"></i>Quick Overview
                    </h5>
                  </div>
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="text-muted">
                        <i className="fas fa-coins text-primary me-2"></i>Active Monitored Tokens
                      </span>
                      <span className="badge bg-primary">NMRC, USDT, BNB</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="text-muted">
                        <i className="fas fa-network-wired text-warning me-2"></i>Network Standard
                      </span>
                      <span className="badge bg-warning text-dark">BSC (BEP20)</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="text-muted">
                        <i className="fas fa-plug text-success me-2"></i>Web3 Connection
                      </span>
                      <span className="badge bg-success">Live RPC Socket</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2">
                      <span className="text-muted">
                        <i className="fas fa-shield-alt text-info me-2"></i>Data Freshness
                      </span>
                      <span className="badge bg-info">Real-time On-chain</span>
                    </div>
                  </div>
                </div>

                {/* Contract Addresses Card */}
                <div className="card custom-card border-0 shadow-sm mb-4">
                  <div className="card-header bg-white border-bottom py-3">
                    <h5 className="card-title mb-0 fw-bold text-dark">
                      <i className="fas fa-file-contract me-2 text-purple"></i>Contract Addresses
                    </h5>
                  </div>
                  <div className="card-body p-3">
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-semibold text-dark small">NMRC Token Contract:</span>
                        <button
                          className="btn btn-sm btn-link p-0 text-primary"
                          onClick={() => copyToClipboard(TOKEN_ADDRESSES.NMRC, "NMRC contract copied!")}
                        >
                          <i className="fas fa-copy me-1"></i>Copy
                        </button>
                      </div>
                      <div className="p-2 bg-light rounded border">
                        <code className="text-primary small text-break">{TOKEN_ADDRESSES.NMRC}</code>
                      </div>
                    </div>

                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-semibold text-dark small">USDT Token Contract:</span>
                        <button
                          className="btn btn-sm btn-link p-0 text-success"
                          onClick={() => copyToClipboard(TOKEN_ADDRESSES.USDT, "USDT contract copied!")}
                        >
                          <i className="fas fa-copy me-1"></i>Copy
                        </button>
                      </div>
                      <div className="p-2 bg-light rounded border">
                        <code className="text-success small text-break">{TOKEN_ADDRESSES.USDT}</code>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Security Advisory */}
                <div className="card custom-card border-0 shadow-sm">
                  <div className="card-body p-3">
                    <div className="alert alert-warning border-0 mb-0" role="alert">
                      <h6 className="fw-bold mb-2">
                        <i className="fas fa-lock me-2"></i>Security Advisory
                      </h6>
                      <ul className="small mb-0 ps-3">
                        <li>Direct blockchain verification guarantees zero cached latency.</li>
                        <li>Ensure BNB balance stays above 0.01 BNB to prevent transaction failures.</li>
                        <li>Never export or expose the master wallet private key.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>



        <ToastContainer />
      </Header>

      <Footer />
    </>
  );
}

export default MasterWallet;